import { useColorScheme, type Theme } from '@mui/material/styles';
import type { EChartsCoreOption } from 'echarts/core';

export type ChartScheme = 'light' | 'dark';

/** The scheme actually on screen. `mode` can be 'system', in which case `systemMode` resolves it. */
export function useChartScheme(): ChartScheme {
  const { mode, systemMode } = useColorScheme();
  const resolved = mode === 'system' ? systemMode : mode;
  return resolved === 'dark' ? 'dark' : 'light';
}

export function schemePalette(theme: Theme, scheme: ChartScheme) {
  // Not theme.palette: under cssVariables that is pinned to the default scheme. See the note above.
  return theme.colorSchemes[scheme]!.palette;
}

export function chartPalette(theme: Theme, scheme: ChartScheme) {
  return schemePalette(theme, scheme).chart;
}

/**
 * One source of colour truth: charts read the same palette tokens the rest of the interface uses, so
 * light and dark follow the color scheme without a second theme definition.
 */
// The return type is `satisfies EChartsCoreOption` rather than an annotation. The annotation still
// validated the shape, but it widened `legend` and `tooltip` onto ECBasicOption's index signature, whose
// type includes `unknown` — and spreading a value typed `unknown` is a TS2698 error, so every caller
// wanting `{ ...base.legend, … }` needed a cast. `satisfies` keeps the same validation and the precise
// inferred shape.
export function baseChartOptions(theme: Theme, scheme: ChartScheme) {
  const palette = schemePalette(theme, scheme);
  const chart = palette.chart;

  return {
    animationDuration: 240,
    color: [...chart.series],
    grid: { bottom: 8, containLabel: true, left: 4, right: 8, top: 8 },
    legend: {
      icon: 'roundRect',
      itemHeight: 8,
      itemWidth: 12,
      // Text wears text tokens, never the series colour.
      textStyle: { color: palette.text.secondary, fontSize: 12 },
      top: 0,
    },
    textStyle: { fontFamily: theme.typography.fontFamily ?? 'sans-serif' },
    tooltip: {
      backgroundColor: palette.background.paper,
      borderColor: palette.divider,
      textStyle: { color: palette.text.primary, fontSize: 12 },
    },
  } satisfies EChartsCoreOption;
}

export function categoryAxis(theme: Theme, scheme: ChartScheme) {
  return {
    axisLabel: { color: chartPalette(theme, scheme).axisLabel, fontSize: 11 },
    axisLine: { lineStyle: { color: schemePalette(theme, scheme).divider } },
    axisTick: { show: false },
    type: 'category' as const,
  };
}

/**
 * `formatTick` is required, not optional. ECharts' default renders the raw plotted number — for money
 * that means an unlabelled float like `4620000`, the exact thing `formatMoney` exists to prevent, on the
 * two surfaces a sighted user actually reads: axis ticks and tooltips. Making it required means a new
 * chart cannot forget it. Charts whose values need no unit still have to say so explicitly.
 */
export function valueAxis(
  theme: Theme,
  scheme: ChartScheme,
  formatTick: (value: number) => string,
) {
  const chart = chartPalette(theme, scheme);

  return {
    axisLabel: { color: chart.axisLabel, fontSize: 11, formatter: formatTick },
    axisLine: { show: false },
    // Hairline solid gridlines, one step off the surface, never dashed.
    splitLine: { lineStyle: { color: chart.grid, type: 'solid' as const, width: 1 } },
    type: 'value' as const,
  };
}
